import { useState } from "react"
import { Formik, Form } from "formik"
import { object, string } from "yup"
import { Plus, X } from "@phosphor-icons/react"
import Modal from "../../../components/Modal"
import TextField from "../../../components/TextField"
import { formatISODate } from "../../../utils/datetime"
import {
  useExperiments,
  useCreateExperiment,
  useUpdateExperiment,
} from "../../../hooks/useExperiments"

const createExperimentValidationSchema = object().shape({
  name: string().label("Name").trim().required().max(100),
})

const ToggleSwitch = ({ checked, disabled, onChange }) => (
  <button
    aria-checked={checked}
    className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 ${
      checked ? "bg-green-600" : "bg-gray-300"
    }`}
    disabled={disabled}
    role="switch"
    type="button"
    onClick={onChange}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
)

const StatusPill = ({ isActive }) => (
  <span
    className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
      isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
    }`}
  >
    {isActive ? "Active" : "Inactive"}
  </span>
)

const CreateExperimentModal = ({ isOpen, onClose }) => {
  const createMutation = useCreateExperiment()

  const handleSubmit = async (values, { resetForm }) => {
    await createMutation.mutateAsync({ name: values.name })
    resetForm()
    onClose()
  }

  return (
    <Modal className="max-w-md" isOpen={isOpen} onClose={onClose}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">New experiment</h2>
        <button
          className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          type="button"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>

      <Formik
        initialValues={{ name: "" }}
        validationSchema={createExperimentValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-4">
            <TextField
              autofocus
              label="Experiment name"
              name="name"
              placeholder="e.g. QrCodeHeading"
            />
            <div className="flex gap-3 pt-2">
              <button
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-slate-50"
                disabled={isSubmitting}
                type="button"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="flex-1 rounded-lg bg-secondary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-secondary_dark disabled:cursor-not-allowed disabled:opacity-40"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? "Creating…" : "Create experiment"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const ExperimentsPanel = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const { data: experiments = [], isLoading } = useExperiments()
  const updateMutation = useUpdateExperiment()

  const handleToggle = (experiment) => {
    updateMutation.mutate({ id: experiment.id, isActive: !experiment.isActive })
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 p-6">
        <div>
          <h2 className="text-base font-semibold text-primary">
            Educational QR code Experiments
          </h2>
        </div>
      </div>

      <div className="border-t border-slate-200">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-400">Loading experiments…</div>
        ) : experiments.length === 0 ? (
          <div className="p-6 text-sm text-gray-400">No experiments yet.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {experiments.map((experiment) => (
              <li
                key={experiment.id}
                className="flex items-center justify-between gap-4 px-6 py-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {experiment.name}
                    </span>
                    <StatusPill isActive={experiment.isActive} />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    Started {formatISODate(experiment.startDate)}
                    {experiment.endDate &&
                      ` · Ended ${formatISODate(experiment.endDate)}`}
                  </p>
                </div>
                <ToggleSwitch
                  checked={experiment.isActive}
                  disabled={updateMutation.isPending}
                  onChange={() => handleToggle(experiment)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateExperimentModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
    </section>
  )
}

export default ExperimentsPanel
